// src/pages/LoginPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config/apiBase";
import { getGoogleClientId } from "../config/googleClientId";
import { postJSON } from "../services/api";
import { isAdminUser, resolveUserRole } from "../utils/userRoles";
import "./LoginPage.css";

export default function LoginPage() {
  const [loginMode, setLoginMode] = useState("student");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [studentError, setStudentError] = useState("");
  const [staffError, setStaffError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleNotEnrolled, setGoogleNotEnrolled] = useState(false);
  const [googleEnrollMessage, setGoogleEnrollMessage] = useState("");
  const navigate = useNavigate();
  const googleButtonRef = useRef(null);
  const googleInitializedRef = useRef(false);
  const googleRenderedRef = useRef(false);
  const googleCredentialHandlerRef = useRef(null);

  async function refreshLoggedInIdentity(studentIdValue, fallbackName = "") {
    const normalizedId = String(studentIdValue || "").trim().toUpperCase();
    if (!normalizedId) return;

    let resolvedName = String(fallbackName || "").trim();
    let resolvedPaymentMade = null;
    let resolvedIconColor = null;

    try {
      const res = await fetch(`${API_BASE_URL}/api/student_account/${encodeURIComponent(normalizedId)}`, {
        cache: "no-store",
      });
      const json = await res.json().catch(() => null);
      if (res.ok && json?.student) {
        resolvedName = String(json.student.name || "").trim() || resolvedName;
        resolvedPaymentMade = Boolean(json.student.payment_made);
        resolvedIconColor = String(json.student.icon_color || "").trim() || null;
      }
    } catch {
      // Fall back to auth payload values below.
    }

    localStorage.setItem("student_id", normalizedId);
    if (resolvedName) {
      localStorage.setItem("student_name", resolvedName);
    } else {
      localStorage.removeItem("student_name");
    }
    if (resolvedPaymentMade !== null) {
      localStorage.setItem("payment_made", resolvedPaymentMade ? "1" : "0");
    }
    if (resolvedIconColor) {
      localStorage.setItem(`student_icon_color_${normalizedId}`, resolvedIconColor);
    }
  }

  googleCredentialHandlerRef.current = async (response) => {
    const credential = String(response?.credential || "").trim();
    if (!credential) {
      setStudentError("Google sign-in did not return a credential.");
      setGoogleNotEnrolled(false);
      setGoogleEnrollMessage("");
      return;
    }

    setStudentError("");
    setGoogleNotEnrolled(false);
    setGoogleEnrollMessage("");

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/google-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_token: credential }),
      });

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) {
        const detail = payload?.detail;
        if (res.status === 403 && detail && typeof detail === "object" && detail.reason === "not_enrolled") {
          setGoogleNotEnrolled(true);
          setGoogleEnrollMessage(detail.message || "This Google account is not enrolled in HIRI yet.");
          setStudentError("");
          return;
        }

        const message =
          typeof detail === "string"
            ? detail
            : detail?.message || payload?.message || "Google sign-in failed.";
        throw new Error(message);
      }

      const auth = payload || {};
      const normalizedEmail = String(auth.email || "").trim().toLowerCase();
      const studentIdValue = String(auth.student_id || auth.username || "").trim().toUpperCase();
      if (!studentIdValue || studentIdValue.includes("@")) {
        throw new Error("Google login did not return a canonical HIRI student ID.");
      }
      const role = resolveUserRole(studentIdValue, auth?.role);
      const isAdmin = isAdminUser({ student_id: studentIdValue, role: auth?.role, is_admin: auth?.is_admin });
      const paymentMade = Boolean(auth?.payment_made);

      await refreshLoggedInIdentity(studentIdValue, auth?.name || auth?.full_name || "");
      localStorage.setItem("role", role);
      localStorage.setItem("is_admin", isAdmin ? "1" : "0");
      localStorage.setItem("payment_made", paymentMade ? "1" : "0");
      localStorage.setItem("auth_provider", "google");
      localStorage.setItem("google_email", normalizedEmail);
      localStorage.setItem("auth_user", JSON.stringify({ ...auth, student_id: studentIdValue, username: studentIdValue }));

      navigate(`/student/dashboard/${encodeURIComponent(studentIdValue)}`);
    } catch (err) {
      console.error("⚠ Google auth error:", err);
      setGoogleNotEnrolled(false);
      setGoogleEnrollMessage("");
      setStudentError(String(err?.message || err));
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setStaffError("");
    setStudentError("");

    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPassword = password.trim();
    if (!trimmedEmail) {
      setStaffError("Please enter your email address.");
      return;
    }
    if (!trimmedPassword) {
      setStaffError("Please enter your password.");
      return;
    }

    try {
      setLoading(true);

      const loginRes = await postJSON("/auth/login", {
        email: trimmedEmail,
        password,
      });

      const user = loginRes?.user || {};
      const role = resolveUserRole(user?.student_id || "", user?.role);
      const isAdmin = isAdminUser(user);
      const paymentMade = Boolean(loginRes?.user?.payment_made);

      await refreshLoggedInIdentity(user?.student_id || "", loginRes?.user?.name || "");
      localStorage.setItem("auth_user", JSON.stringify(user));
      localStorage.setItem("role", role);
      localStorage.setItem("is_admin", isAdmin ? "1" : "0");
      localStorage.setItem("payment_made", paymentMade ? "1" : "0");

      if (!paymentMade) {
        setShowPaymentModal(true);
        return;
      }

      if (role === "tutor") {
        navigate("/tutor/needs-review");
        return;
      }

      navigate(`/student/dashboard/${encodeURIComponent(String(user?.student_id || "").trim().toUpperCase())}`);
    } catch (err) {
      console.error("⚠ Auth error:", err);
      const message = String(err?.message || err);
      setStaffError(message === "Invalid email or password"
        ? "Either your email or password is incorrect."
        : message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loginMode !== "student") {
      googleRenderedRef.current = false;
      return undefined;
    }

    const isDev = process.env.NODE_ENV !== "production";
    const clientId = getGoogleClientId();
    const maskedClientId = clientId
      ? clientId.length <= 8
        ? `${clientId.slice(0, 2)}…${clientId.slice(-2)}`
        : `${clientId.slice(0, 4)}…${clientId.slice(-4)}`
      : "";
    const debugGoogleSignin = (stage, details = {}) => {
      if (!isDev || typeof console === "undefined" || typeof console.debug !== "function") return;
      console.debug("[google-signin]", stage, {
        origin: window.location.origin,
        clientIdPresent: Boolean(clientId),
        maskedClientId,
        ...details,
      });
    };

    debugGoogleSignin("effect:start", {
      googleObjectPresent: Boolean(window.google?.accounts?.id),
    });
    if (!clientId) {
      debugGoogleSignin("effect:missing-client-id");
      return undefined;
    }

    let cancelled = false;

    const initializeGoogle = () => {
      if (cancelled) {
        return;
      }

      const google = window.google?.accounts?.id;
      if (!google) {
        debugGoogleSignin("initialize:google-missing");
        return;
      }

      if (!googleInitializedRef.current) {
        google.initialize({
          client_id: clientId,
          use_fedcm_for_prompt: false,
          callback: (response) => {
            googleCredentialHandlerRef.current?.(response);
          },
        });
        googleInitializedRef.current = true;
        debugGoogleSignin("initialize:called", {
          initializeCalled: true,
        });
      }

      if (!googleRenderedRef.current && googleButtonRef.current && typeof google.renderButton === "function") {
        googleButtonRef.current.innerHTML = "";
        const buttonWidth = Math.max(
          280,
          Math.min(420, googleButtonRef.current.parentElement?.clientWidth || 360)
        );
        google.renderButton(googleButtonRef.current, {
          theme: "filled_blue",
          size: "large",
          shape: "pill",
          text: "signin_with",
          logo_alignment: "left",
          width: buttonWidth,
        });
        googleRenderedRef.current = true;
      }
    };

    if (window.google?.accounts?.id) {
      initializeGoogle();
      return undefined;
    }

    const scriptId = "google-gsi-client";
    let script = document.getElementById(scriptId);
    const onLoad = () => initializeGoogle();

    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = onLoad;
      script.onerror = () => {
        if (!cancelled) {
          setStudentError("Google sign-in could not load.");
          debugGoogleSignin("script:error");
        }
      };
      document.head.appendChild(script);
    } else {
      script.addEventListener("load", onLoad, { once: true });
    }

    const buttonContainer = googleButtonRef.current;

    return () => {
      cancelled = true;
      if (script) {
        script.removeEventListener?.("load", onLoad);
      }
      if (buttonContainer) {
        buttonContainer.innerHTML = "";
      }
      googleRenderedRef.current = false;
    };
  }, [loginMode]);

  const switchToStudentLogin = () => {
    setLoginMode("student");
    setPassword("");
    setStudentError("");
    setStaffError("");
    setGoogleNotEnrolled(false);
    setGoogleEnrollMessage("");
  };

  return (
    <div className="ll-wrap">
      <header className="ll-nav">
        <div className="ll-nav-left">
          <div className="ll-logo-wrap reveal" style={{ "--delay": "0s" }}>
            <img src={process.env.PUBLIC_URL + "/hirilogo.png"} alt="HIRI logo" className="ll-logo" />
          </div>
          <span className="ll-brand reveal" style={{ "--delay": ".05s" }}>HOW I REMEMBER IT</span>
        </div>
        <div className="ll-nav-right">
          <button className="ll-link reveal" style={{ "--delay": ".1s" }} onClick={() => navigate("/")}>Back</button>
        </div>
      </header>

      <section className="ll-card reveal" style={{ "--delay": ".15s" }}>
        {loginMode === "student" ? (
          <div className="ll-student-panel">
            <div className="ll-hero-copy">
              <h2 className="ll-title reveal" style={{ "--delay": ".2s" }}>Log in to HIRI</h2>
              <p className="ll-sub reveal" style={{ "--delay": ".25s" }}>Enter your email address and password to continue.</p>
            </div>

            <form className="ll-form ll-student-form" onSubmit={handleLogin}>
              <label className="ll-label" htmlFor="student-login-id">
                Email
              </label>
              <input
                className="ll-input"
                autoComplete="email"
                id="student-login-id"
                name="email"
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <label className="ll-label" htmlFor="student-login-password">
                Password
              </label>
              <div className="ll-password-field">
                <input
                className="ll-input"
                autoComplete="current-password"
                id="student-login-password"
                placeholder="Enter your password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
                <button
                  className="ll-password-toggle"
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-controls="student-login-password"
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword(visible => !visible)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    {showPassword ? <>
                      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
                      <circle cx="12" cy="12" r="3" />
                    </> : <>
                      <path d="M3 12s3.2 6 9 6 9-6 9-6M6 16l-2 3m8-1v3m6-5 2 3" />
                    </>}
                  </svg>
                </button>
              </div>

              <Link className="ll-forgot-password" to="/forgot-password">Forgot password?</Link>

              {(staffError || studentError) && <p role="alert" className="ll-error ll-error-inline">{staffError || studentError}</p>}

              <button type="submit" disabled={loading} className={`ll-cta ${loading ? "loading" : ""}`}>
                {loading ? "Logging in..." : "Continue"}
              </button>
            </form>

            <div className="ll-divider ll-divider-wide" aria-hidden="true">
              <span>OR</span>
            </div>

            <div className="ll-google-shell">
              <div ref={googleButtonRef} className="ll-google-button" aria-label="Sign in with Google" />
              {googleNotEnrolled && <p className="ll-error">{googleEnrollMessage}</p>}
            </div>

            <div className="ll-enroll-cta">
              <div className="ll-enroll-text">New student?</div>
              <button className="ll-secondary" type="button" onClick={() => navigate("/enroll")}>
                Enrol now
              </button>
            </div>

          </div>
        ) : (
          <div className="ll-staff-panel">
            <button className="ll-back-link" type="button" onClick={switchToStudentLogin}>
              ← Back to Student Login
            </button>
            <h2 className="ll-title reveal" style={{ "--delay": ".2s" }}>Staff Login</h2>

            <form onSubmit={handleLogin} className="ll-form">
              <label className="ll-label" htmlFor="staff-login-identifier">Email</label>
              <input
                className="ll-input"
                autoComplete="email"
                id="staff-login-identifier"
                name="email"
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <label className="ll-label" htmlFor="staff-login-password">Password</label>
              <input
                className="ll-input"
                autoComplete="current-password"
                id="staff-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
              />

              {staffError && <p className="ll-error">{staffError}</p>}

              <button type="submit" disabled={loading} className={`ll-cta ${loading ? "loading" : ""}`}>
                {loading ? "Logging in..." : "Continue"}
              </button>
            </form>
          </div>
        )}
      </section>

      {showPaymentModal && (
        <div className="ll-modal-overlay" role="dialog" aria-modal="true">
          <div className="ll-modal">
            <h3>Payment being processed</h3>
            <p>Your payment is being processed. You’ll be able to access the student portal once it’s confirmed.</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
              <button className="ll-cta" type="button" onClick={() => setShowPaymentModal(false)}>Okay</button>
            </div>
          </div>
        </div>
      )}

      <footer className="ll-footer">© 2026 How I Remember It</footer>
    </div>
  );
}
